import type { $Fetch } from 'ofetch';
import type {
  Tutor, UpsertTutorCommand,
  Assunto, UpsertAssuntoCommand,
  Solicitacao, SolicitacaoMensagem, ListSolicitacoesParams,
} from '@/domain/model/Tutoria';
import type { TutorGateway, AssuntoGateway, SolicitacaoGateway } from '@/application/port/out/TutoriaGateways';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** HTTP gateway de Tutores. Authorities `tutoria:tutor:read|manage`. */
export class HttpTutorGateway implements TutorGateway {
  private readonly path = '/api/v1/tutores';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<Tutor[]> {
    const res = await this.client<ApiResponse<Tutor[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertTutorCommand): Promise<Tutor> {
    const res = await this.client<ApiResponse<Tutor>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertTutorCommand): Promise<Tutor> {
    const res = await this.client<ApiResponse<Tutor>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async arquivar(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** HTTP gateway de Assuntos de tutoria. Authorities `tutoria:assunto:read|manage`. */
export class HttpAssuntoGateway implements AssuntoGateway {
  private readonly path = '/api/v1/tutoria/assuntos';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<Assunto[]> {
    const res = await this.client<ApiResponse<Assunto[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertAssuntoCommand): Promise<Assunto> {
    const res = await this.client<ApiResponse<Assunto>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertAssuntoCommand): Promise<Assunto> {
    const res = await this.client<ApiResponse<Assunto>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
}

/** HTTP gateway de Solicitações de tutoria. Authorities `tutoria:solicitacao:read|manage`. */
export class HttpSolicitacaoGateway implements SolicitacaoGateway {
  private readonly path = '/api/v1/tutoria/solicitacoes';
  constructor(private readonly client: $Fetch) {}
  async list(p: ListSolicitacoesParams): Promise<Solicitacao[]> {
    const params: Record<string, string> = {};
    if (p.status) params['status'] = p.status;
    if (p.matriculaId) params['matriculaId'] = p.matriculaId;
    const res = await this.client<ApiResponse<Solicitacao[]>>(this.path, { method: 'GET', params });
    assertApiSuccess(res); return res.data;
  }
  async assumir(id: string, tutorId: string): Promise<Solicitacao> {
    const res = await this.client<ApiResponse<Solicitacao>>(
      `${this.path}/${encodeURIComponent(id)}/assumir`, { method: 'POST', params: { tutorId } });
    assertApiSuccess(res); return res.data;
  }
  async listMensagens(id: string): Promise<SolicitacaoMensagem[]> {
    const res = await this.client<ApiResponse<SolicitacaoMensagem[]>>(
      `${this.path}/${encodeURIComponent(id)}/mensagens`, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async responder(id: string, corpo: string): Promise<SolicitacaoMensagem> {
    const res = await this.client<ApiResponse<SolicitacaoMensagem>>(
      `${this.path}/${encodeURIComponent(id)}/mensagens`, { method: 'POST', body: { corpo } });
    assertApiSuccess(res); return res.data;
  }
  async encerrar(id: string): Promise<Solicitacao> {
    const res = await this.client<ApiResponse<Solicitacao>>(
      `${this.path}/${encodeURIComponent(id)}/encerrar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
}
